import React from 'react';
import { NavLink, Link, useNavigate } from 'react-router-dom';
import { SidebarTrigger } from '@/components/ui/sidebar';
import { cn } from '@/lib/utils';
import { useIsMobile } from '@/hooks/use-mobile';
import { useTheme } from '@/hooks/use-theme';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuLabel, 
  DropdownMenuSeparator, 
  DropdownMenuTrigger 
} from '@/components/ui/dropdown-menu';
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { toast } from 'sonner';

import { useQuery } from '@tanstack/react-query';
import type { ApiResponse, DocRecord } from '@shared/types';

export function TopNav() {
  const isMobile = useIsMobile();
  const { theme, toggleTheme } = useTheme();
  const [open, setOpen] = React.useState(false);
  const [time, setTime] = React.useState(new Date());
  const navigate = useNavigate();

  const { data: docs } = useQuery({
    queryKey: ['docs'],
    queryFn: () => fetch('/api/docs').then(res => res.json() as Promise<ApiResponse<DocRecord[]>>).then(res => res.data || [])
  });

  React.useEffect(() => {
    const timer = setInterval(() => setTime(new Date()), 1000);
    const down = (e: KeyboardEvent) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((open) => !open);
      }
    };
    document.addEventListener("keydown", down);
    return () => {
      clearInterval(timer);
      document.removeEventListener("keydown", down);
    };
  }, []);

  const getThemeIcon = () => {
    if (theme === 'light') return 'light_mode';
    if (theme === 'dark') return 'dark_mode';
    return 'brightness_auto';
  };

  return (
    <header className="bg-surface border-b border-outline-variant docked full-width top-0 flex items-center w-full px-4 md:px-margin-page h-[64px] shrink-0 z-[60] transition-colors">
      <div className="flex items-center gap-4 md:gap-8 h-full shrink-0">
        <div className="md:hidden">
          <SidebarTrigger asChild>
            <button className="p-2 text-primary hover:bg-surface-container-low rounded-lg transition-colors">
              <span className="material-symbols-outlined">menu</span>
            </button>
          </SidebarTrigger>
        </div>
        
        <Link to="/" className="flex items-center gap-2">
           <span className="font-label-caps text-label-caps tracking-[0.2em] text-primary uppercase font-bold">DocForge</span>
        </Link>
      </div>

      {/* Search Input - Centered */}
      <div className="flex-1 max-w-xl mx-auto px-4 hidden md:block">
        <div 
          onClick={() => setOpen(true)}
          className="group flex items-center gap-3 bg-surface-container-low border border-outline-variant rounded-full px-5 py-1.5 cursor-pointer hover:border-primary/50 transition-all shadow-sm active:scale-[0.99]"
        >
          <span className="material-symbols-outlined text-primary text-[20px]">search</span>
          <span className="flex-1 font-body-md text-on-surface-variant text-sm py-1 opacity-60 group-hover:opacity-100">Search manuscripts, archives, or templates...</span>
          <div className="flex items-center gap-1 bg-surface-container-high px-2 py-0.5 rounded border border-outline-variant/30">
            <span className="text-[10px] font-label-caps text-on-surface-variant">⌘</span>
            <span className="text-[10px] font-label-caps text-on-surface-variant">K</span>
          </div>
        </div>
      </div>

      {/* Trailing Actions - Status & Time */}
      <div className="flex items-center gap-4 md:gap-6 ml-auto h-full">
        <div className="hidden lg:flex items-center gap-6 border-r border-outline-variant pr-6 h-1/2">
           <div className="flex flex-col items-end">
              <div className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-teal animate-pulse"></span>
                <span className="font-label-caps text-[9px] uppercase tracking-widest text-teal font-bold">Forge Active</span>
              </div>
              <span className="font-label-caps text-[10px] text-on-surface-variant uppercase tracking-wider">v1.2.4-stable</span>
           </div>

           <div className="flex flex-col items-end min-w-[70px]">
              <span className="font-label-caps text-[12px] text-on-surface font-bold brand-monospace leading-none">
                {time.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false })}
              </span>
              <span className="font-label-caps text-[9px] text-on-surface-variant uppercase tracking-widest leading-tight mt-1">
                {time.toLocaleDateString([], { month: 'short', day: 'numeric' })}
              </span>
           </div>
        </div>

        <div className="flex items-center gap-2">
          <button 
            onClick={toggleTheme}
            aria-label="Toggle theme" 
            className="p-2.5 text-primary hover:bg-surface-container-high rounded-full transition-all active:scale-90 border border-transparent hover:border-outline-variant/30"
          >
            <span className="material-symbols-outlined text-[20px]">{getThemeIcon()}</span>
          </button>
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-3 ml-2 hover:bg-surface-container-high pl-1 pr-3 py-1 rounded-full transition-all active:scale-95 border border-transparent hover:border-outline-variant/30">
                <div className="w-8 h-8 rounded-full bg-primary-container border border-outline-variant overflow-hidden shadow-sm ring-2 ring-transparent hover:ring-primary/20 transition-all">
                  <img 
                    alt="User profile" 
                    className="w-full h-full object-cover" 
                    src="https://lh3.googleusercontent.com/aida-public/AB6AXuDssxiIHblDQLODLe3DnzW-7NG2fcsqPhRSOGcMSI7XfzwJwVS6laJlmhImJu_6rMEXHPq1T4vLsZQ-lZrF25urh4OREK0qSEodZw8RbtWI_KYSFt86lqv7YQH1CuxfZOmfh30cEbZ80rZEW_evi2sFn8DigrrxANsveDUX7xotu_G_h0z_63JcBHQG9UhJxrQhQh6n-qyE0srGSyHewjKJWG9t3h_D7K_n7W6n4H5OaKVaP1EDGiEsPCUgtA9C7JuwaCVWN1E4FJhe"
                  />
                </div>
                {!isMobile && (
                  <div className="hidden lg:flex flex-col items-start">
                    <span className="font-label-caps text-[10px] text-on-surface font-bold uppercase tracking-widest leading-none mb-0.5">Marcus Thorne</span>
                    <span className="font-label-caps text-[8px] text-on-surface-variant uppercase tracking-wider leading-none">Master Editor</span>
                  </div>
                )}
                <span className="material-symbols-outlined text-on-surface-variant text-[16px] hidden lg:block">expand_more</span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56 bg-surface border-outline-variant shadow-xl mt-2 p-1">
              <DropdownMenuLabel className="font-label-caps text-[9px] text-primary uppercase tracking-[0.2em] px-3 py-2">Master Scriptorium</DropdownMenuLabel>
              <DropdownMenuSeparator className="bg-outline-variant/30 mx-1" />
              <DropdownMenuItem onClick={() => navigate('/settings')} className="font-body-md text-sm cursor-pointer focus:bg-primary/5 rounded-md mx-1 py-2.5">
                 <span className="material-symbols-outlined text-[18px] mr-3 text-primary/70">person</span>
                 Profile Details
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate('/settings')} className="font-body-md text-sm cursor-pointer focus:bg-primary/5 rounded-md mx-1 py-2.5">
                 <span className="material-symbols-outlined text-[18px] mr-3 text-primary/70">settings</span>
                 System Settings
              </DropdownMenuItem>
              <DropdownMenuSeparator className="bg-outline-variant/30 mx-1" />
              <DropdownMenuItem onClick={() => toast.success('Logged out.')} className="font-body-md text-sm cursor-pointer text-error focus:bg-error/10 rounded-md mx-1 py-2.5">
                 <span className="material-symbols-outlined text-[18px] mr-3">logout</span>
                 Relinquish Session
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <CommandDialog open={open} onOpenChange={setOpen}>
        <CommandInput placeholder="Search manuscripts, templates..." className="font-body-md border-none focus:ring-0" />
        <CommandList className="bg-surface border-t border-outline-variant">
          <CommandEmpty className="py-6 text-center font-body-md text-on-surface-variant">No records found.</CommandEmpty>
          
          {docs && docs.length > 0 && (
            <CommandGroup heading="Archives" className="font-label-caps text-[10px] text-primary uppercase tracking-widest p-4">
              {docs.map(doc => (
                <CommandItem 
                  key={doc.slug} 
                  onSelect={() => {
                    navigate(`/doc/${doc.slug}`);
                    setOpen(false);
                  }}
                  className="font-body-md p-3 cursor-pointer hover:bg-surface-container-high rounded-lg flex items-center gap-3"
                >
                  <span className="material-symbols-outlined text-[20px]">
                    {doc.status === 'archived' ? 'verified' : 'history_edu'}
                  </span>
                  <div className="flex flex-col">
                    <span>{doc.title}</span>
                    <span className="text-[10px] opacity-50 uppercase tracking-wider">{doc.status} · {doc.slug}</span>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          )}

          <CommandGroup heading="Quick Actions" className="font-label-caps text-[10px] text-primary uppercase tracking-widest p-4">
            <CommandItem onSelect={() => { navigate('/'); setOpen(false); }} className="font-body-md p-3 cursor-pointer hover:bg-surface-container-high rounded-lg flex items-center gap-3">
              <span className="material-symbols-outlined text-[20px]">add</span>
              <span>New Manuscript</span>
            </CommandItem>
            <CommandItem onSelect={() => { navigate('/library'); setOpen(false); }} className="font-body-md p-3 cursor-pointer hover:bg-surface-container-high rounded-lg flex items-center gap-3">
              <span className="material-symbols-outlined text-[20px]">book_5</span>
              <span>Open Library</span>
            </CommandItem>
          </CommandGroup>
        </CommandList>
      </CommandDialog>
    </header>
  );
}